import React, { useEffect, useMemo, useState, FC } from 'react';
import {
  FaqsHeaderSection,
  FaqsDetailsSection,
  FaqsDetailsSectionSkeleton,
} from 'views/rebranded-module/information-module/FaqsViewV2/components';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useSelector } from 'redux/app/store';
import { useDispatch } from 'react-redux';
import { setLoading } from 'redux/features/ui-slice';
import PATHS from 'lib/navigation/paths';
import { FAQS_TYPES_PATHS, FAQS_TYPES } from 'lib/utils/CONSTANTS';
import generateMetaData from 'lib/utils/generateMetaData';
import Head from 'next/head';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import { useTranslation } from 'next-i18next';
import { PAGE_STATES } from 'views/rebranded-module/information-module/FaqsViewV2/utils/constants';
import { snakeToCamelCase } from 'views/rebranded-module/common/utils/snakeToCamelCase';
import { GetFaqsParams } from 'views/rebranded-module/information-module/FaqsViewV2/FaqsViewV2.types';
import { LazyLoad } from 'views/common/components';

const FaqsViewV2: FC<any> = (props) => {
  const { serverData, ssrRender, originalKey } = props;
  const showMembership = useSelector(
    (state) => state.globalData.showMembership,
  );
  const { t } = useTranslation('routes');
  const content = useContent();
  const appNavigator = useAppNavigator();
  const handleErrorResponse = useErrorsHandler();
  const { slug: type, q } = appNavigator.query;
  const dispatch = useDispatch();
  // states
  const [searchQuery, setSearchQuery] = useState(q);
  const [popupState, setPopupState] = useState();
  let initialPageState;
  if (!type && !q) {
    initialPageState = PAGE_STATES.INDEX;
  } else if (type) {
    initialPageState = PAGE_STATES.SELECTED_CATEGORIES;
  } else if (q) {
    initialPageState = PAGE_STATES.SEARCH;
  }
  const [pageState, setPageState] = useState(initialPageState);
  // there 3 page statue index, selectedCategories , search
  const [faqsData, setFaqsData] = useState(serverData);
  const faqsTabs = useMemo(
    () =>
      faqsData?.faq_item_types
        ? faqsData.faq_item_types.map((faqType) => {
            return {
              id: faqType.id,
              systemIdentifier: faqType.sym_id,
              pathIdentifier: FAQS_TYPES_PATHS[faqType.sym_id],
              path: PATHS.faqs.withSlug(FAQS_TYPES_PATHS[faqType.sym_id]),
              label: content.FAQSPage[snakeToCamelCase(faqType.sym_id)],
              value: faqType.sym_id,
            };
          })
        : [],
    [faqsData?.faq_item_types],
  );

  const selectedTab =
    type && faqsTabs?.find((tab) => tab.pathIdentifier === originalKey);

  const getCategorizedFaqs = (faqsArray) => {
    if (!faqsArray) return [];
    const categories = {};
    faqsArray.forEach((faq) => {
      const { faq_item_type: faqType = {}, question, answer } = faq;
      const { id, sym_id: faqTypeName } = faqType;
      const typeName = content.FAQSPage[`${snakeToCamelCase(faqTypeName)}Faqs`];
      if (!categories[typeName])
        categories[typeName] = { id, categories: typeName, questions: [] };
      categories[typeName].questions.push({ question, answer });
    });
    const categorizedQuestions = Object.keys(categories).map(
      (cat) => categories[cat],
    );
    return categorizedQuestions;
  };
  const getFaqs = ({ query, systemIdentifier }: GetFaqsParams = {}) => {
    dispatch(setLoading(true));

    axios
      .get(API.faqs, {
        params: systemIdentifier
          ? {
              type: systemIdentifier,
              q: query,
              preferred_seo_identifer: PATHS.faqs.withSlug(
                t(`routes:${FAQS_TYPES_PATHS[systemIdentifier]}`),
              ).url,
            }
          : {
              type: systemIdentifier,
              q: query,
            },
      })
      .then((res) => {
        if (showMembership) {
          setFaqsData(res?.data);
        } else {
          const tempFaqsData = {
            ...res.data,
            faq_item_types: res.data.faq_item_types.filter(
              (faqType) =>
                faqType.sym_id !== FAQS_TYPES.membership &&
                faqType.sym_id !== FAQS_TYPES.membership_e_gift_cards,
            ),
          };
          setFaqsData(tempFaqsData);
        }
      })
      .catch((error) => {
        handleErrorResponse(error, false);
      })
      .finally(() => dispatch(setLoading(false)));
  };
  // handlers
  const handleSearchQueryClick = (content) => {
    setSearchQuery(content);
    if (content) {
      setPageState(PAGE_STATES.SEARCH);
      appNavigator.push(PATHS.faqs.index({ q: content }));
    } else {
      setPageState(PAGE_STATES.INDEX);
      appNavigator.push(PATHS.faqs.index());
    }
    getFaqs({ query: content });
  };

  const handleBackButtonClick = () => {
    if (ssrRender) return;
    setPageState(PAGE_STATES.INDEX);
    setSearchQuery('');
    getFaqs();
  };

  const handleTypeTabClick = (systemIdentifier) => {
    if (ssrRender) return;
    setPageState(PAGE_STATES.SELECTED_CATEGORIES);
    getFaqs({ systemIdentifier });
  };

  const handleBreadCrumbsClick = () => {
    if (ssrRender) return;
    setPageState(PAGE_STATES.INDEX);
    setSearchQuery('');
    getFaqs();
  };

  const popUpHandler = () => {
    if (!type && !q) {
      handleBackButtonClick();
    } else if (type) {
      handleTypeTabClick(selectedTab?.systemIdentifier);
    } else if (q) {
      handleSearchQueryClick(q);
    }
    setPopupState(undefined);
  };

  // useEffects
  useEffect(() => {
    const popstateHandler = (e) => {
      setPopupState(e.state);
    };
    if (!serverData) {
      window.addEventListener('popstate', popstateHandler);
      const initialSystemIdentifier = type
        ? Object.keys(FAQS_TYPES_PATHS).find(
            (key) => FAQS_TYPES_PATHS[key] === originalKey,
          )
        : null;

      getFaqs({ query: q, systemIdentifier: initialSystemIdentifier });
    }
    return () => window.removeEventListener('popstate', popstateHandler);
  }, []);

  useEffect(() => {
    if (!popupState) return;
    popUpHandler();
  }, [q, type]);

  useEffect(() => {
    if (!faqsData || serverData) return;
    const hash = appNavigator?.asPath?.split('#')?.[1];
    hash &&
      document.getElementById(hash) &&
      document.getElementById(hash).scrollIntoView();
  }, [faqsData]);

  return (
    <>
      {faqsData?.seo_values && (
        <Head>{generateMetaData(faqsData.seo_values)}</Head>
      )}
      <FaqsHeaderSection
        pageState={pageState}
        typeObject={selectedTab}
        handleBackButtonClick={handleBackButtonClick}
        handleBreadCrumbsClick={handleBreadCrumbsClick}
      />
      <LazyLoad skeleton={<FaqsDetailsSectionSkeleton />} dataLoaded>
        <FaqsDetailsSection
          pageState={pageState}
          faqs={getCategorizedFaqs(faqsData?.faq_items)}
          searchKeyword={searchQuery}
          handleSearchQueryClick={handleSearchQueryClick}
          handleTypeTabClick={handleTypeTabClick}
          typeId={selectedTab?.id}
          faqsTabs={faqsTabs}
        />
      </LazyLoad>
    </>
  );
};

export { FaqsViewV2 };
